/**
 * Compact product-card quick add: full-width add button + optional variant listbox.
 */
(function () {
  if (typeof customElements === 'undefined') return;

  /** CSS custom properties on `.product-card-compact-quick-add__panel--fixed-layout` (see compact quick-add CSS). */
  const COMPACT_PANEL_LAYOUT_CUSTOM_PROPERTIES = [
    '--compact-panel-top',
    '--compact-panel-bottom',
    '--compact-panel-left',
    '--compact-panel-width',
    '--compact-panel-max-height',
  ];

  function getCartSectionTargets() {
    return [
      { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
    ];
  }

  function extractSectionInnerHtml(sectionHtml, selector) {
    const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const matchedElement = parsedDocument.querySelector(selector);
    return matchedElement ? matchedElement.innerHTML : '';
  }

  function refreshCartUiFromAddResponse(parsedCartAddResponse) {
    getCartSectionTargets().forEach((sectionConfig) => {
      const sectionRootElement = document.getElementById(sectionConfig.id);
      if (!sectionRootElement || !parsedCartAddResponse.sections || !parsedCartAddResponse.sections[sectionConfig.section]) {
        return;
      }
      const targetElement = sectionRootElement.querySelector(sectionConfig.selector) || sectionRootElement;
      targetElement.innerHTML = extractSectionInnerHtml(
        parsedCartAddResponse.sections[sectionConfig.section],
        sectionConfig.selector
      );

      if (sectionConfig.id === 'Cart-Drawer') {
        document.getElementById('Cart-Drawer')?.notesToggle?.();
        document.getElementById('Cart-Drawer')?.removeProductEvent?.();
      }

      if (sectionConfig.id === 'Cart' && typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedCartAddResponse);
      }
    });

    const cartDrawerElement = document.getElementById('Cart-Drawer');
    if (cartDrawerElement) {
      cartDrawerElement.classList.add('active');
      document.body.classList.add('open-cart');
      document.body.classList.add('open-cc');
      const recommendationsFullWidth = cartDrawerElement.querySelector('.product-recommendations--full');
      if (recommendationsFullWidth) {
        recommendationsFullWidth.classList.add('active');
      }
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('cart-drawer:open');
      }
    }
  }

  class ProductCardCompactQuickAdd extends HTMLElement {
    connectedCallback() {
      this.variantsJsonScript = this.querySelector('[data-compact-variants]');
      try {
        this.variants = this.variantsJsonScript ? JSON.parse(this.variantsJsonScript.textContent.trim()) : [];
      } catch (parseError) {
        this.variants = [];
      }

      this.addToCartButton = this.querySelector('.product-card-compact-quick-add__add');
      this.addToCartButtonTextElement = this.querySelector('.product-card-compact-quick-add__add-text');
      this.variantsToggleButton = this.querySelector('.product-card-compact-quick-add__variants-toggle');
      this.variantToggleTitleElement = this.querySelector('.product-card-compact-quick-add__variants-toggle-label');
      this.variantOptionsPanel = this.querySelector('.product-card-compact-quick-add__panel');
      this.variantOptionButtons = this.variantOptionsPanel
        ? Array.from(this.variantOptionsPanel.querySelectorAll('.product-card-compact-quick-add__option'))
        : [];

      this.isPreorderFromMetafield = this.dataset.productPreorder === 'true';
      this.isProductAvailableInDataset = this.dataset.productAvailable === 'true';
      this.chooseOptionsAriaPrefix = this.dataset.chooseOptionsAccessibilityPrefix || '';

      this.moneyWithCurrencyFormat = window.theme?.settings?.money_with_currency_format || '${{amount}}';

      this.boundOnDocumentClickCapture = this.onDocumentClickCapture.bind(this);
      this.boundOnDocumentKeydownCapture = this.onDocumentKeydownCapture.bind(this);
      this.boundOnAddToCartClick = this.onAddToCartClick.bind(this);
      this.boundOnVariantToggleClick = this.onVariantToggleClick.bind(this);
      this.boundOnVariantOptionClick = this.onVariantOptionClick.bind(this);
      this.boundOnWindowScrollMaybeClosePanel = this.onWindowScrollMaybeClosePanel.bind(this);
      this.boundOnWindowResizeRelayoutPanel = this.onWindowResizeRelayoutPanel.bind(this);

      this.addToCartButton?.addEventListener('click', this.boundOnAddToCartClick);
      this.variantsToggleButton?.addEventListener('click', this.boundOnVariantToggleClick);
      this.variantOptionButtons.forEach((optionButton) => {
        optionButton.addEventListener('click', this.boundOnVariantOptionClick);
      });

      const initialVariantIdNumber = Number(this.dataset.initialVariantId) || null;
      this.currentlySelectedVariant =
        this.variants.find((variant) => variant.id === initialVariantIdNumber) || this.variants[0] || null;
      if (this.currentlySelectedVariant) {
        this.syncAddButtonForVariant(this.currentlySelectedVariant, false);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundOnDocumentClickCapture, true);
      document.removeEventListener('keydown', this.boundOnDocumentKeydownCapture, true);
      this.addToCartButton?.removeEventListener('click', this.boundOnAddToCartClick);
      this.variantsToggleButton?.removeEventListener('click', this.boundOnVariantToggleClick);
      this.variantOptionButtons.forEach((optionButton) => {
        optionButton.removeEventListener('click', this.boundOnVariantOptionClick);
      });
      this.closeVariantPanel();
    }

    getPriceRootInCard() {
      const productCardElement = this.closest('product-card');
      return productCardElement?.querySelector('[data-product-card-price-root]') || null;
    }

    getPrimaryImageInCard() {
      const productCardElement = this.closest('product-card');
      if (!productCardElement) return null;
      return (
        productCardElement.querySelector('.product-card--featured-image-link img.product-primary-image') || null
      );
    }

    getFeaturedImageLinkInCard() {
      const productCardElement = this.closest('product-card');
      if (!productCardElement) return null;
      return productCardElement.querySelector('a.product-card--featured-image-link') || null;
    }

    /**
     * Keeps the card image link in sync with the selected variant (?variant=) using the URL the theme rendered.
     */
    syncFeaturedLinkForVariant(variant) {
      const linkElement = this.getFeaturedImageLinkInCard();
      if (!linkElement || !variant) return;
      const hrefAttribute = linkElement.getAttribute('href');
      if (!hrefAttribute) return;
      try {
        const parsedUrl = new URL(hrefAttribute, window.location.origin);
        parsedUrl.searchParams.set('variant', String(variant.id));
        linkElement.setAttribute('href', `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
      } catch {
        /* ignore invalid href */
      }
    }

    /**
     * After a variant is chosen in the listbox: swap the card main image when `featured_image_card` exists; otherwise no-op.
     * Does not touch `.product-secondary-image` (hover). No-op when there is no `<img.product-primary-image>` (e.g. video card).
     */
    syncPrimaryImageWithVariant(variant) {
      const featuredImageCardPayload = variant?.featured_image_card;
      if (!featuredImageCardPayload || typeof featuredImageCardPayload !== 'object') return;
      const primaryImageUrl = featuredImageCardPayload.src;
      const primaryImageSrcset = featuredImageCardPayload.srcset;
      if (!primaryImageUrl || !primaryImageSrcset) return;
      const primaryImageElement = this.getPrimaryImageInCard();
      if (!primaryImageElement) return;
      primaryImageElement.setAttribute('alt', featuredImageCardPayload.alt || '');
      if (featuredImageCardPayload.width) {
        primaryImageElement.setAttribute('width', String(featuredImageCardPayload.width));
      }
      if (featuredImageCardPayload.height) {
        primaryImageElement.setAttribute('height', String(featuredImageCardPayload.height));
      }
      primaryImageElement.setAttribute('src', primaryImageUrl);
      primaryImageElement.setAttribute('srcset', primaryImageSrcset);
      primaryImageElement.setAttribute('data-srcset', primaryImageSrcset);
      if (typeof lazySizes !== 'undefined' && lazySizes.loader && typeof lazySizes.loader.unveil === 'function') {
        lazySizes.loader.unveil(primaryImageElement);
      }
    }

    formatMoneyWithTheme(cents) {
      if (typeof formatMoney !== 'function') return String(cents);
      return formatMoney(cents, this.moneyWithCurrencyFormat);
    }

    syncPriceDisplayForVariant(variant) {
      const priceRootElement = this.getPriceRootInCard();
      if (!priceRootElement || !variant) return;

      const salePriceElement = priceRootElement.querySelector('[data-js-price]');
      const compareAtPriceAmountElement = priceRootElement.querySelector('[data-js-compare-price]');
      const compareAtWrapper = priceRootElement.querySelector('del');
      const variantPriceCents = variant.price;
      const variantCompareAtPriceCents = variant.compare_at_price;

      if (salePriceElement) {
        salePriceElement.textContent = this.formatMoneyWithTheme(variantPriceCents);
        const variantIsOnSale =
          variantCompareAtPriceCents && variantCompareAtPriceCents > 0 && variantCompareAtPriceCents > variantPriceCents;
        salePriceElement.classList.toggle('discounted', !!variantIsOnSale);
      }

      if (compareAtPriceAmountElement && compareAtWrapper) {
        if (
          variantCompareAtPriceCents &&
          variantCompareAtPriceCents > 0 &&
          variantCompareAtPriceCents > variantPriceCents
        ) {
          compareAtWrapper.hidden = false;
          compareAtPriceAmountElement.textContent = this.formatMoneyWithTheme(variantCompareAtPriceCents);
        } else {
          compareAtWrapper.hidden = true;
          compareAtPriceAmountElement.textContent = '';
        }
      }

      const unitPriceContainerElement = priceRootElement.querySelector('.unit-price');
      if (unitPriceContainerElement) {
        if (variant.unit_price && variant.unit_price_measurement) {
          unitPriceContainerElement.classList.remove('hidden');
          const unitPriceSpanElements = unitPriceContainerElement.querySelectorAll('span');
          if (unitPriceSpanElements[0]) {
            unitPriceSpanElements[0].textContent = this.formatMoneyWithTheme(variant.unit_price);
          }
          const unitPriceMeasurement = variant.unit_price_measurement;
          if (unitPriceSpanElements[2]) {
            let referenceUnitLabelText = '';
            if (unitPriceMeasurement.reference_value && Number(unitPriceMeasurement.reference_value) !== 1) {
              referenceUnitLabelText = String(unitPriceMeasurement.reference_value);
            }
            referenceUnitLabelText += unitPriceMeasurement.reference_unit || '';
            unitPriceSpanElements[2].textContent = referenceUnitLabelText;
          }
        } else {
          unitPriceContainerElement.classList.add('hidden');
        }
      }
    }

    syncAddButtonForVariant(variant, shouldUpdateDisplayedPrice) {
      if (!this.addToCartButton || !variant) return;
      this.addToCartButton.dataset.variantId = String(variant.id);

      const productShowsPreorderAccessibilityState = this.isPreorderFromMetafield && this.isProductAvailableInDataset;
      const variantIsAvailableForPurchase = !!variant.available;

      this.addToCartButton.disabled = !variantIsAvailableForPurchase;
      if (variantIsAvailableForPurchase) {
        this.addToCartButton.removeAttribute('aria-disabled');
      } else {
        this.addToCartButton.setAttribute('aria-disabled', 'true');
      }

      const variantAccessibilityStrings = window.theme?.variantStrings || {};
      let addToCartButtonLabel = variantAccessibilityStrings.addToCart || 'Add to cart';
      if (!variant.available) {
        addToCartButtonLabel = variantAccessibilityStrings.soldOut || 'Sold out';
      } else if (productShowsPreorderAccessibilityState) {
        addToCartButtonLabel = variantAccessibilityStrings.preOrder || 'Pre-order';
      }
      if (this.addToCartButtonTextElement) {
        this.addToCartButtonTextElement.textContent = addToCartButtonLabel;
      }

      if (shouldUpdateDisplayedPrice) {
        this.syncPriceDisplayForVariant(variant);
      }

      this.syncVariantToggleLabelAndAria(variant);
      this.syncFeaturedLinkForVariant(variant);
    }

    syncVariantToggleLabelAndAria(variant) {
      if (!variant) return;
      if (this.variantToggleTitleElement) {
        this.variantToggleTitleElement.textContent = variant.title;
      }
      if (this.variantsToggleButton && this.chooseOptionsAriaPrefix) {
        this.variantsToggleButton.setAttribute('aria-label', `${this.chooseOptionsAriaPrefix}: ${variant.title}`);
      }
    }

    syncListboxSelectionAria(selectedVariantId) {
      this.variantOptionButtons.forEach((optionButton) => {
        const optionVariantId = Number(optionButton.dataset.variantId);
        const isThisOptionSelected = optionVariantId === selectedVariantId;
        optionButton.setAttribute('aria-selected', isThisOptionSelected ? 'true' : 'false');
      });
    }

    onVariantOptionClick(event) {
      const clickedOptionButton = event.currentTarget;
      if (clickedOptionButton.getAttribute('aria-disabled') === 'true') return;
      const selectedVariantId = Number(clickedOptionButton.dataset.variantId);
      const matchedVariant = this.variants.find((variant) => variant.id === selectedVariantId);
      if (!matchedVariant) return;
      this.currentlySelectedVariant = matchedVariant;
      this.syncListboxSelectionAria(selectedVariantId);
      this.syncAddButtonForVariant(matchedVariant, true);
      this.syncPrimaryImageWithVariant(matchedVariant);
      this.closeVariantPanel();
      this.variantsToggleButton?.focus();
    }

    isVariantPanelOpen() {
      return this.variantOptionsPanel && !this.variantOptionsPanel.hidden;
    }

    openVariantPanel() {
      if (!this.variantOptionsPanel || !this.variantsToggleButton) return;
      // Apply geometry while the panel is still `hidden` so the first paint is already fixed
      // (avoid one frame in-flow / unpositioned, which reads as a jump).
      this.layoutFloatingVariantPanel();
      this.variantOptionsPanel.hidden = false;
      this.variantsToggleButton.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.boundOnDocumentClickCapture, true);
      document.addEventListener('keydown', this.boundOnDocumentKeydownCapture, true);
      this.attachPanelDismissListeners();
      requestAnimationFrame(() => {
        this.layoutFloatingVariantPanel();
        const firstEnabledOptionButton =
          this.variantOptionButtons.find((optionElement) => optionElement.getAttribute('aria-disabled') !== 'true') ||
          this.variantOptionButtons[0];
        firstEnabledOptionButton?.focus();
      });
    }

    closeVariantPanel() {
      this.detachPanelDismissListeners();
      document.removeEventListener('click', this.boundOnDocumentClickCapture, true);
      document.removeEventListener('keydown', this.boundOnDocumentKeydownCapture, true);
      if (!this.variantOptionsPanel || !this.variantsToggleButton) return;
      this.clearPanelLayoutStyles();
      this.variantOptionsPanel.hidden = true;
      this.variantsToggleButton.setAttribute('aria-expanded', 'false');
    }

    attachPanelDismissListeners() {
      window.addEventListener('scroll', this.boundOnWindowScrollMaybeClosePanel, true);
      window.addEventListener('resize', this.boundOnWindowResizeRelayoutPanel);
    }

    detachPanelDismissListeners() {
      window.removeEventListener('scroll', this.boundOnWindowScrollMaybeClosePanel, true);
      window.removeEventListener('resize', this.boundOnWindowResizeRelayoutPanel);
    }

    onWindowScrollMaybeClosePanel(event) {
      if (!this.isVariantPanelOpen()) return;
      const panelElement = this.variantOptionsPanel;
      const scrollEventTarget = event.target;
      if (panelElement && scrollEventTarget instanceof Node && panelElement.contains(scrollEventTarget)) {
        return;
      }
      this.closeVariantPanel();
      this.variantsToggleButton?.focus();
    }

    onWindowResizeRelayoutPanel() {
      if (!this.isVariantPanelOpen()) return;
      this.layoutFloatingVariantPanel();
    }

    layoutFloatingVariantPanel() {
      const toggleButton = this.variantsToggleButton;
      const panelElement = this.variantOptionsPanel;
      if (!toggleButton || !panelElement) return;
      const toggleRect = toggleButton.getBoundingClientRect();
      const viewportGutterPx = 8;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const preferredPanelWidthPx = Math.max(260, toggleRect.width);
      const panelWidthPx = Math.min(preferredPanelWidthPx, viewportWidth - viewportGutterPx * 2);
      const spaceBelowTogglePx = viewportHeight - toggleRect.bottom - viewportGutterPx * 2;
      const spaceAboveTogglePx = toggleRect.top - viewportGutterPx * 2;
      const maxPanelHeightWhenBelowPx = Math.max(100, Math.min(spaceBelowTogglePx, viewportHeight * 0.55));
      const maxPanelHeightWhenAbovePx = Math.max(100, Math.min(spaceAboveTogglePx, viewportHeight * 0.55));
      const shouldAnchorPanelAboveToggle =
        maxPanelHeightWhenBelowPx < 140 && spaceAboveTogglePx > spaceBelowTogglePx;

      let panelLeftPx = toggleRect.right - panelWidthPx;
      panelLeftPx = Math.max(
        viewportGutterPx,
        Math.min(panelLeftPx, viewportWidth - panelWidthPx - viewportGutterPx)
      );

      const panelLayoutStyle = panelElement.style;
      COMPACT_PANEL_LAYOUT_CUSTOM_PROPERTIES.forEach((propertyName) => panelLayoutStyle.removeProperty(propertyName));
      panelElement.classList.add('product-card-compact-quick-add__panel--fixed-layout');

      panelLayoutStyle.setProperty('--compact-panel-left', `${panelLeftPx}px`);
      panelLayoutStyle.setProperty('--compact-panel-width', `${panelWidthPx}px`);

      if (shouldAnchorPanelAboveToggle) {
        panelLayoutStyle.setProperty('--compact-panel-max-height', `${maxPanelHeightWhenAbovePx}px`);
        panelLayoutStyle.setProperty(
          '--compact-panel-bottom',
          `${viewportHeight - toggleRect.top + viewportGutterPx}px`
        );
      } else {
        panelLayoutStyle.setProperty('--compact-panel-max-height', `${maxPanelHeightWhenBelowPx}px`);
        panelLayoutStyle.setProperty('--compact-panel-top', `${toggleRect.bottom + viewportGutterPx}px`);
      }

      this.reorderSelectedOptionForAnchor(shouldAnchorPanelAboveToggle);
    }

    reorderSelectedOptionForAnchor(anchorAboveToggle) {
      this.variantOptionButtons.forEach((optionButton) => {
        optionButton.style.removeProperty('order');
        if (optionButton.getAttribute('aria-selected') === 'true') {
          optionButton.style.order = anchorAboveToggle ? '999' : '-1';
        }
      });
    }

    clearOptionOrderStyles() {
      this.variantOptionButtons.forEach((optionButton) => {
        optionButton.style.removeProperty('order');
      });
    }

    clearPanelLayoutStyles() {
      if (!this.variantOptionsPanel) return;
      const panelElement = this.variantOptionsPanel;
      panelElement.classList.remove('product-card-compact-quick-add__panel--fixed-layout');
      COMPACT_PANEL_LAYOUT_CUSTOM_PROPERTIES.forEach((propertyName) => {
        panelElement.style.removeProperty(propertyName);
      });
      this.clearOptionOrderStyles();
    }

    onDocumentClickCapture(event) {
      if (!this.isVariantPanelOpen()) return;
      if (this.contains(event.target)) return;
      this.closeVariantPanel();
    }

    onDocumentKeydownCapture(event) {
      if (!this.isVariantPanelOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeVariantPanel();
        this.variantsToggleButton?.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const activeElement = document.activeElement;
      const focusIsInsidePanelOrToggle =
        (this.variantOptionsPanel && this.variantOptionsPanel.contains(activeElement)) ||
        activeElement === this.variantsToggleButton;
      if (!focusIsInsidePanelOrToggle) return;
      const enabledOptionButtons = this.variantOptionButtons.filter(
        (optionElement) => optionElement.getAttribute('aria-disabled') !== 'true'
      );
      if (!enabledOptionButtons.length) return;
      event.preventDefault();
      let focusedOptionIndex = enabledOptionButtons.indexOf(activeElement);
      if (focusedOptionIndex < 0) focusedOptionIndex = 0;
      else if (event.key === 'ArrowDown') {
        focusedOptionIndex = Math.min(enabledOptionButtons.length - 1, focusedOptionIndex + 1);
      } else {
        focusedOptionIndex = Math.max(0, focusedOptionIndex - 1);
      }
      enabledOptionButtons[focusedOptionIndex].focus();
    }

    onVariantToggleClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.isVariantPanelOpen()) {
        this.closeVariantPanel();
      } else {
        this.openVariantPanel();
      }
    }

    onAddToCartClick(event) {
      event.preventDefault();
      if (this.addToCartButton.disabled || this.addToCartButton.getAttribute('aria-disabled') === 'true') return;

      const variantIdToAdd = this.addToCartButton.dataset.variantId;
      if (!variantIdToAdd) return;

      this.addToCartButton.classList.add('loading');
      this.addToCartButton.setAttribute('aria-disabled', 'true');

      const addToCartFormData = new FormData();
      addToCartFormData.append('id', variantIdToAdd);
      addToCartFormData.append('quantity', 1);
      addToCartFormData.append(
        'sections',
        getCartSectionTargets().map((sectionConfig) => sectionConfig.section)
      );
      addToCartFormData.append('sections_url', window.location.pathname);

      fetch(`${theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: addToCartFormData,
      })
        .then((response) => response.json())
        .then((parsedCartAddResponse) => {
          if (parsedCartAddResponse.status) return;
          refreshCartUiFromAddResponse(parsedCartAddResponse);
          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart:item-added', {
              product: parsedCartAddResponse.hasOwnProperty('items')
                ? parsedCartAddResponse.items[0]
                : parsedCartAddResponse,
            });
          }
        })
        .catch((fetchError) => console.error(fetchError))
        .finally(() => {
          this.addToCartButton.classList.remove('loading');
          if (!this.addToCartButton.disabled) {
            this.addToCartButton.removeAttribute('aria-disabled');
          } else {
            this.addToCartButton.setAttribute('aria-disabled', 'true');
          }
        });
    }
  }

  if (!customElements.get('product-card-compact-quick-add')) {
    customElements.define('product-card-compact-quick-add', ProductCardCompactQuickAdd);
  }
})();
